import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Save, ArrowLeft, History, RotateCcw, X, Download } from "lucide-react";

import MatchKeysBlock from "../components/rule-builder/MatchKeysBlock";
import TolerancesBlock from "../components/rule-builder/TolerancesBlock";
import DateToleranceBlock from "../components/rule-builder/DateToleranceBlock";
import AggregationBlock from "../components/rule-builder/AggregationBlock";
import FuzzyBlock from "../components/rule-builder/FuzzyBlock";
import IgnoreBlock from "../components/rule-builder/IgnoreBlock";
import TransformsBlock from "../components/rule-builder/TransformsBlock";  // legacy — kept for backward compat display
import EnrichmentsBlock from "../components/rule-builder/EnrichmentsBlock"; // legacy
import ConditionsBlock from "../components/rule-builder/ConditionsBlock";

const MATCH_TYPES = [
  { value: "exact", label: "Exact Match" },
  { value: "numeric_tolerance", label: "Numeric Tolerance" },
  { value: "date_tolerance", label: "Date Tolerance" },
  { value: "aggregate", label: "Aggregation (1:N)" },
  { value: "fuzzy", label: "Fuzzy (Text Similarity)" },
  { value: "ignore", label: "Ignore / Non-contributing" },
];

function RuleBlock({ rule, index, total, lookupTables, cleanupTables, regexPatterns, onChange, onDelete, onMove, impact, runId }) {
  const [expanded, setExpanded] = useState(true);

  const onTypeChange = (newType) => {
    const patch = { ...rule, match_type: newType };
    if (newType === "numeric_tolerance" && !(rule.tolerance_keys || []).length) {
      patch.tolerance_keys = [{ file_a_col: "", file_b_col: "" }];
      patch.tolerance_type = rule.tolerance_type || "absolute";
      patch.tolerance_value = rule.tolerance_value ?? 0.01;
    }
    if (newType === "aggregate" && !(rule.agg_config?.join_key_pairs || []).length) {
      patch.agg_config = {
        agg_side: "b", group_by_cols: [], agg_col: "", agg_function: "sum",
        join_key_pairs: [{ single_col: "", agg_col: "" }],
        value_col_single: "", tolerance_type: "absolute", tolerance_value: 0.01,
        ...(rule.agg_config || {}),
      };
    }
    if (newType === "date_tolerance" && !(rule.date_keys || []).length) {
      patch.date_keys = [{ file_a_col: "", file_b_col: "" }];
      patch.date_tolerance_days = rule.date_tolerance_days ?? 3;
    }
    if (newType === "fuzzy" && !rule.fuzzy_config) {
      patch.fuzzy_config = {
        column_pairs: [{ a_col: "", b_col: "" }],
        threshold: 80,
        algo: "token_set",
      };
    }
    if (newType === "ignore" && !rule.ignore_config) {
      patch.ignore_config = {
        side: "both",
        expression: {
          type: "group", op: "AND",
          children: [{ type: "condition", col: "", op: "contains", value: "" }],
        },
        reason: "non_contributing",
        remarks: "",
      };
    }
    onChange(patch);
  };

  const enabled = rule.enabled !== false;
  const toggleEnabled = () => onChange({ ...rule, enabled: !enabled });

  return (
    <div className="rule-block" data-testid={`rule-block-${index}`}
      style={{ opacity: enabled ? 1 : 0.55, background: enabled ? undefined : "repeating-linear-gradient(135deg, transparent 0 8px, rgba(0,0,0,0.02) 8px 16px)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
          <span style={{ width: "1.5rem", height: "1.5rem", borderRadius: "9999px", background: enabled ? "var(--sage)" : "#9CA3AF", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>{rule.sequence}</span>
          <input data-testid={`rule-name-${index}`} className="recon-input" value={rule.name}
            onChange={e => onChange({ ...rule, name: e.target.value })}
            placeholder="Rule name"
            style={{ fontWeight: 500, fontSize: "0.9rem", border: "none", background: "transparent", padding: "0.25rem", outline: "none", width: "14rem", textDecoration: enabled ? "none" : "line-through", color: enabled ? undefined : "var(--text-muted)" }} />
          <select data-testid={`rule-type-${index}`} className="recon-input"
            value={rule.match_type || "exact"}
            onChange={e => onTypeChange(e.target.value)}
            style={{ fontSize: "0.78rem", padding: "0.25rem 0.5rem" }}>
            {MATCH_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          {!enabled && (
            <span data-testid={`rule-disabled-pill-${index}`} style={{ fontSize: "0.65rem", padding: "0.15rem 0.5rem", borderRadius: "9999px", background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Disabled
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <button
            data-testid={`rule-toggle-${index}`}
            onClick={toggleEnabled}
            title={enabled ? "Disable rule (will be skipped on next run)" : "Enable rule"}
            style={{
              position: "relative",
              width: "2.1rem", height: "1.15rem",
              background: enabled ? "var(--sage)" : "#D1D5DB",
              border: "none", borderRadius: "9999px",
              cursor: "pointer", padding: 0, flexShrink: 0,
              transition: "background 0.15s ease",
            }}>
            <span style={{
              position: "absolute", top: "2px", left: enabled ? "calc(100% - 0.95rem - 2px)" : "2px",
              width: "0.95rem", height: "0.95rem",
              background: "white", borderRadius: "9999px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              transition: "left 0.15s ease",
            }} />
          </button>
          <button onClick={() => onMove(index, -1)} disabled={index === 0} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", opacity: index === 0 ? 0.3 : 1 }}><ArrowUp size={14} /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", opacity: index === total - 1 ? 0.3 : 1 }}><ArrowDown size={14} /></button>
          <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
          <button data-testid={`del-rule-${index}`} onClick={() => onDelete(index)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={14} /></button>
        </div>
      </div>

      <RuleImpactBar impact={impact} ruleIndex={index} runId={runId} ruleName={rule.name} />

      {expanded && (
        <div style={{ marginTop: "1rem" }}>
          {rule.match_type !== "aggregate" && rule.match_type !== "fuzzy" && rule.match_type !== "ignore" && (
            <>
              <MatchKeysBlock rule={rule} index={index} onChange={onChange}
                listKey="match_keys"
                label={rule.match_type === "numeric_tolerance" ? "Key columns (must match exactly)" : "Column pairs to match"}
                testidPrefix="mk" minOne addLabel="Add pair" />
              {rule.match_type === "numeric_tolerance" && (
                <TolerancesBlock rule={rule} index={index} onChange={onChange} />
              )}
              {rule.match_type === "date_tolerance" && (
                <DateToleranceBlock rule={rule} index={index} onChange={onChange} />
              )}
            </>
          )}

          {rule.match_type === "aggregate" && (
            <AggregationBlock rule={rule} index={index} onChange={onChange} />
          )}

          {rule.match_type === "fuzzy" && (
            <FuzzyBlock rule={rule} index={index} onChange={onChange} />
          )}

          {rule.match_type === "ignore" && (
            <IgnoreBlock rule={rule} index={index} onChange={onChange} />
          )}

          {rule.match_type !== "aggregate" && rule.match_type !== "fuzzy" && rule.match_type !== "ignore" && (
            <div style={{ padding: "0.5rem 0.875rem", background: "#FFFBEB", borderRadius: "0.375rem", border: "1px solid #FCD34D", fontSize: "0.78rem", color: "#92400E", margin: "0.5rem 0" }}>
              Pre-processing transforms (trim, regex, lookup, etc.) now live in <strong>Transform Sets</strong> — assign one per file in the Reconciliation Wizard. Existing transforms on this rule still run for active runs.
            </div>
          )}

          {rule.match_type !== "ignore" && (
            <ConditionsBlock rule={rule} index={index} onChange={onChange} />
          )}
        </div>
      )}
    </div>
  );
}

function RuleImpactBar({ impact, ruleIndex, runId, ruleName }) {
  const navigate = useNavigate();
  if (!impact) return null;
  const m = impact.matched || 0;
  const p = impact.partial || 0;
  const ig = impact.ignored || 0;
  const total = m + p + ig;
  if (total === 0) {
    return (
      <div data-testid={`impact-bar-${ruleIndex}`} style={{ marginTop: "0.4rem", fontSize: "0.65rem", color: "var(--text-muted)", fontStyle: "italic" }}>
        · No rows caught by this rule in the last run
      </div>
    );
  }
  const mpct = (m / total) * 100;
  const ppct = (p / total) * 100;
  const ipct = (ig / total) * 100;

  // Drill-down: segment/legend click → Results page, pre-filtered to this rule
  // on the relevant tab. Only active when we actually have a run_id + rule name.
  const canDrill = Boolean(runId && ruleName);
  const go = (tab) => {
    if (!canDrill) return;
    navigate(`/results/${runId}?tab=${tab}&rule=${encodeURIComponent(ruleName)}`);
  };
  const segStyle = (bg, width) => ({
    width: `${width}%`, background: bg,
    cursor: canDrill ? "pointer" : "default",
    transition: "filter 120ms ease",
  });
  const chipBtn = (color, tab) => ({
    color, background: "none", border: "none", padding: 0,
    cursor: canDrill ? "pointer" : "default",
    font: "inherit", fontWeight: 600,
    textDecoration: canDrill ? "none" : "none",
  });

  return (
    <div data-testid={`impact-bar-${ruleIndex}`} style={{ marginTop: "0.4rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <div style={{ flex: 1, height: "6px", background: "#F3F4F6", borderRadius: 999, overflow: "hidden", display: "flex" }}>
        {m > 0 && (
          <div
            data-testid={`impact-seg-matched-${ruleIndex}`}
            onClick={() => go("matched")}
            title={canDrill ? `Matched: ${m} — click to view rows` : `Matched: ${m}`}
            style={segStyle("var(--sage)", mpct)} />
        )}
        {p > 0 && (
          <div
            data-testid={`impact-seg-partial-${ruleIndex}`}
            onClick={() => go("partial")}
            title={canDrill ? `Partial: ${p} — click to view rows` : `Partial: ${p}`}
            style={segStyle("#F59E0B", ppct)} />
        )}
        {ig > 0 && (
          <div
            data-testid={`impact-seg-ignored-${ruleIndex}`}
            onClick={() => go("ignored")}
            title={canDrill ? `Ignored: ${ig} — click to view rows` : `Ignored: ${ig}`}
            style={segStyle("#6B7280", ipct)} />
        )}
      </div>
      <div style={{ display: "flex", gap: "0.5rem", fontSize: "0.65rem", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>
        {m > 0 && (
          <button
            data-testid={`impact-chip-matched-${ruleIndex}`}
            disabled={!canDrill} onClick={() => go("matched")}
            style={chipBtn("var(--sage)", "matched")}>● {m}</button>
        )}
        {p > 0 && (
          <button
            data-testid={`impact-chip-partial-${ruleIndex}`}
            disabled={!canDrill} onClick={() => go("partial")}
            style={chipBtn("#D97706", "partial")}>● {p}</button>
        )}
        {ig > 0 && (
          <button
            data-testid={`impact-chip-ignored-${ruleIndex}`}
            disabled={!canDrill} onClick={() => go("ignored")}
            style={chipBtn("#4B5563", "ignored")}>● {ig}</button>
        )}
        <span style={{ color: "var(--text-muted)" }}>last run{canDrill ? " · click to drill down" : ""}</span>
      </div>
    </div>
  );
}


export default function RuleBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deptId, setDeptId] = useState("");
  const [departments, setDepartments] = useState([]);
  const [lookupTables, setLookupTables] = useState([]);
  const [cleanupTables, setCleanupTables] = useState([]);
  const [regexPatterns, setRegexPatterns] = useState([]);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [impact, setImpact] = useState(null);   // {by_rule: {rule_name: {matched, partial, ignored}}, run_name, run_at}

  const loadRuleSet = async () => {
    if (!id) return;
    const r = await api.get(`/rule-sets/${id}`);
    setName(r.data.name);
    setDescription(r.data.description || "");
    setDeptId(r.data.department_id || "");
    setRules(r.data.rules || []);
  };

  const loadImpact = async () => {
    if (!id || id === "new") return;
    try {
      const r = await api.get(`/rule-sets/${id}/impact`);
      setImpact(r.data);
    } catch (e) {
      setImpact(null);
    }
  };

  useEffect(() => { loadImpact(); /* eslint-disable-next-line */ }, [id]);

  const loadVersions = async () => {
    if (!id) return;
    setLoadingVersions(true);
    try {
      const r = await api.get(`/rule-sets/${id}/versions`);
      setVersions(r.data);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRollback = async (versionNumber) => {
    if (!window.confirm(`Rollback to version ${versionNumber}? Current state will be saved as a new version first.`)) return;
    try {
      await api.post(`/rule-sets/${id}/rollback/${versionNumber}`);
      setShowVersions(false);
      await loadRuleSet();
      await loadVersions();
    } catch (e) {
      alert(`Rollback failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  useEffect(() => {
    if (user?.role === "superadmin") {
      api.get("/admin/departments").then(r => setDepartments(r.data));
    }
    api.get("/lookups/").then(r => setLookupTables(r.data)).catch(() => {});
    api.get("/cleanups/").then(r => setCleanupTables(r.data)).catch(() => {});
    api.get("/regex/").then(r => setRegexPatterns(r.data)).catch(() => {});
    if (id && id !== "new") {
      loadRuleSet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  const addRule = () => {
    const seq = rules.length + 1;
    setRules(prev => [...prev, {
      id: "", sequence: seq, name: `Rule ${seq}`,
      match_keys: [{ file_a_col: "", file_b_col: "" }],
      transforms_a: [], transforms_b: [], match_type: "exact",
    }]);
  };

  const updateRule = (i, updated) => setRules(prev => prev.map((r, idx) => idx === i ? updated : r));
  const deleteRule = (i) => setRules(prev => prev.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, sequence: idx + 1 })));

  const moveRule = (i, dir) => {
    const newRules = [...rules];
    const target = i + dir;
    if (target < 0 || target >= newRules.length) return;
    [newRules[i], newRules[target]] = [newRules[target], newRules[i]];
    setRules(newRules.map((r, idx) => ({ ...r, sequence: idx + 1 })));
  };

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError("Rule set name is required"); return; }
    if (rules.length === 0) { setError("Add at least one rule"); return; }
    for (const r of rules) {
      if (!r.name.trim()) { setError("All rules must have a name"); return; }
      if (r.match_type === "fuzzy") {
        const fpairs = (r.fuzzy_config?.column_pairs) || [];
        if (!fpairs.length) { setError(`Rule "${r.name}": add at least one fuzzy column pair`); return; }
        for (const p of fpairs) {
          if (!p.a_col?.trim() || !p.b_col?.trim()) { setError(`Rule "${r.name}": every fuzzy pair needs a File A and File B column`); return; }
          const t = p.threshold ?? r.fuzzy_config?.threshold;
          if (t == null || Number.isNaN(Number(t)) || Number(t) < 1 || Number(t) > 100) {
            setError(`Rule "${r.name}": pair "${p.a_col} ↔ ${p.b_col}" needs a confidence 1–100`);
            return;
          }
        }
      } else if (r.match_type === "ignore") {
        const expr = r.ignore_config?.expression;
        const legacyConds = r.ignore_config?.conditions;
        if (!expr && !(legacyConds && legacyConds.length)) {
          setError(`Rule "${r.name}": add at least one ignore condition`); return;
        }
        const walkErr = (node) => {
          if (!node) return null;
          if (node.type === "group") {
            const kids = node.children || [];
            if (!kids.length) return `empty ${node.op || "AND"} group`;
            for (const k of kids) {
              const e = walkErr(k);
              if (e) return e;
            }
            return null;
          }
          if (!node.col?.trim()) return "a condition is missing its column name";
          const needsValue = !["is_empty", "is_not_empty"].includes(node.op);
          if (needsValue && (node.value == null || String(node.value).trim() === "")) {
            return `condition on "${node.col}" needs a value`;
          }
          return null;
        };
        const err = expr ? walkErr(expr) : null;
        if (err) { setError(`Rule "${r.name}": ${err}`); return; }
      } else if (r.match_type !== "aggregate") {
        for (const mk of (r.match_keys || [])) {
          if (!mk.file_a_col?.trim() || !mk.file_b_col?.trim()) { setError(`Rule "${r.name}": all match key pairs must have column names`); return; }
        }
      } else {
        const a = r.agg_config || {};
        if (!a.agg_col || !(a.group_by_cols || []).length || !a.value_col_single || !(a.join_key_pairs || []).length) {
          setError(`Rule "${r.name}": aggregation rule needs agg_col, group_by_cols, value_col_single, and join_key_pairs`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = { name, description, rules, department_id: deptId || null };
      if (id) {
        await api.put(`/rule-sets/${id}`, payload);
        navigate("/dept-admin");
      } else {
        const r = await api.post("/rule-sets/", payload);
        navigate(`/rule-builder/${r.data.id}`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "900px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}><ArrowLeft size={20} strokeWidth={1.5} /></button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", color: "var(--text-primary)", margin: 0 }}>{id ? "Edit Rule Set" : "New Rule Set"}</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>Define matching rules in priority order</p>
          </div>
          <button data-testid="save-ruleset-btn" className="btn-primary" onClick={handleSave} disabled={saving} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Save size={14} /> {saving ? "Saving..." : "Save Rule Set"}
          </button>
          {id && (
            <button data-testid="versions-btn" className="btn-secondary" onClick={() => { loadVersions(); setShowVersions(true); }}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem" }}>
              <History size={14} /> Versions
            </button>
          )}
          {id && (
            <button data-testid="export-rs-btn" className="btn-secondary"
              onClick={async () => {
                try {
                  const res = await api.get(`/rule-sets/${id}/export`, { responseType: "blob" });
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
                  link.download = `rule_set_${name.replace(/\s+/g, "_") || id}.json`;
                  link.click();
                  URL.revokeObjectURL(link.href);
                } catch (e) {
                  alert("Export failed: " + (e.response?.data?.detail || e.message));
                }
              }}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem" }}>
              <Download size={14} /> Export
            </button>
          )}
        </div>

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.875rem", color: "#991B1B" }}>{error}</div>
        )}

        <div className="recon-card" style={{ padding: "1.25rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: user?.role === "superadmin" ? "1fr 1fr 1fr" : "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>Rule Set Name *</label>
              <input data-testid="rs-name-input" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bank Statement Reconciliation" />
            </div>
            <div>
              <label style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>Description</label>
              <input className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
            </div>
            {user?.role === "superadmin" && (
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>Department</label>
                <select className="recon-input" style={{ width: "100%", boxSizing: "border-box" }} value={deptId} onChange={e => setDeptId(e.target.value)}>
                  <option value="">— All Departments —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
          <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", color: "var(--text-primary)", margin: 0 }}>
            Rules <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", fontWeight: 400 }}>— tried in sequence, first match wins</span>
          </h3>
          <button data-testid="add-rule-btn" className="btn-secondary" onClick={addRule} style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.75rem" }}>
            <Plus size={14} /> Add Rule
          </button>
        </div>

        {rules.length === 0 ? (
          <div className="recon-card" style={{ padding: "3rem", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>No rules yet. Click "Add Rule" to define your first matching rule.</p>
          </div>
        ) : (
          <div>
            {rules.map((rule, i) => (
              <RuleBlock key={i} rule={rule} index={i} total={rules.length}
                lookupTables={lookupTables} cleanupTables={cleanupTables} regexPatterns={regexPatterns}
                impact={impact?.by_rule?.[rule.name]}
                runId={impact?.run_id}
                onChange={(updated) => updateRule(i, updated)} onDelete={deleteRule} onMove={moveRule} />
            ))}
          </div>
        )}

        {impact?.run_id && (
          <div data-testid="impact-summary"
            style={{ marginTop: "0.75rem", padding: "0.5rem 0.85rem", background: "white", border: "1px dashed var(--border-default)", borderRadius: 8, fontSize: "0.72rem", color: "var(--text-muted)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <span>
              Rule impact from <strong style={{ color: "var(--text-primary)" }}>{impact.run_name}</strong>
              {impact.run_at ? ` · ${new Date(impact.run_at).toLocaleString()}` : ""}
            </span>
            <span style={{ display: "flex", gap: "0.8rem", fontFamily: "JetBrains Mono, monospace" }}>
              {impact.manual_count > 0 && <span>manual: {impact.manual_count}</span>}
              <span>unmatched A: {impact.unmatched_a || 0}</span>
              <span>unmatched B: {impact.unmatched_b || 0}</span>
            </span>
          </div>
        )}

        <div style={{ marginTop: "1.25rem", padding: "0.875rem 1rem", background: "var(--sage-light)", borderRadius: "0.5rem", border: "1px solid var(--sage-border)" }}>
          <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
            <strong>Match types:</strong> <em>Exact</em> — keys must match exactly. <em>Numeric Tolerance</em> — keys match, value columns within ±X or ±X%. <em>Date Tolerance</em> — keys match, date columns within ±N days. <em>Aggregation</em> — group + sum rows on one side to match a single total on the other.
            &nbsp;<strong>Conditions</strong> restrict a rule to rows matching specific column predicates.
          </p>
        </div>
      </div>

      {/* Versions Modal */}
      {showVersions && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)" }} onClick={() => setShowVersions(false)} />
          <div data-testid="versions-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "640px", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", margin: 0 }}>Version History</h3>
              <button data-testid="versions-close" onClick={() => setShowVersions(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
            </div>
            {loadingVersions ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>Loading...</div>
            ) : versions.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.875rem" }}>
                No previous versions. A snapshot is created automatically every time you save.
              </div>
            ) : (
              <div>
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                  Each save creates a snapshot of the previous state. Roll back to restore earlier rules.
                </p>
                {versions.map(v => (
                  <div key={v.id} data-testid={`version-${v.version_number}`} className="recon-card" style={{ padding: "0.75rem 0.875rem", marginBottom: "0.5rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.2rem" }}>
                          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)" }}>v{v.version_number}</span>
                          <span style={{ fontSize: "0.7rem", padding: "0.1rem 0.5rem", background: v.snapshot_reason?.startsWith("pre-rollback") ? "#FEF3C7" : "var(--sage-light)", color: v.snapshot_reason?.startsWith("pre-rollback") ? "#92400E" : "var(--sage)", borderRadius: "9999px" }}>
                            {v.snapshot_reason}
                          </span>
                        </div>
                        <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0 0 0.2rem" }}>
                          {v.description || <em>no description</em>} — {(v.rules || []).length} rule{(v.rules || []).length !== 1 ? "s" : ""}
                        </p>
                        <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", margin: 0 }}>
                          {v.snapshot_at ? new Date(v.snapshot_at).toLocaleString() : "—"}
                        </p>
                      </div>
                      <button data-testid={`rollback-${v.version_number}`} onClick={() => handleRollback(v.version_number)}
                        className="btn-secondary"
                        style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", padding: "0.25rem 0.625rem", flexShrink: 0 }}>
                        <RotateCcw size={11} /> Rollback
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
